import { PgRelationalQuery } from "drizzle-orm/pg-core/query-builders/query";
import { isEqual } from "lodash";

import { createLogger } from "@/logger";

const log = createLogger("query");

import { useEffect, useMemo, useRef, useState } from "react";

import { flags } from "@/config";
import { firstTableNamedInQuery, summarizeQuery } from "@/db/summarizeQuery.ts";
import { TableEventManager, TableName } from "@/models/eventing/TableEventManager.ts";
import { useThrottledCallback } from "@/models/hooks/useThrottledCallback.ts";
import { QueryMetricsService } from "@/services/QueryMetricsService";

const DEFAULT_THROTTLE_INTERVAL_MS = 250;

export function getTablesFromRelationalQuery<TResult>(query: PgRelationalQuery<TResult>): TableName[] {
  return [firstTableNamedInQuery(query.toSQL().sql) as TableName];
}

export function useReactiveRelationalQuery<TResult extends any, Params extends unknown[]>(
  queryFactory: (...params: Params) => PgRelationalQuery<TResult>,
  params: Params,
  debugName?: string,
  throttleIntervalMs = DEFAULT_THROTTLE_INTERVAL_MS,
): TResult | null {
  const [currentResult, setCurrentResult] = useState<TResult | null>();

  // Only update params if they've changed. Otherwise React will treat each array as a new reference and will re-run the
  // query every time.
  //
  // See https://stackoverflow.com/a/66653729
  const currentParams = useRef<unknown[]>(params);

  if (!isEqual(params, currentParams.current)) {
    currentParams.current = params;
  }

  const builtQuery = useMemo(() => queryFactory(...params), [queryFactory, params]);
  const querySql = useMemo(() => builtQuery.toSQL().sql, [builtQuery]);
  const querySummary = useMemo(() => summarizeQuery(querySql), [querySql]);

  const performQuery = (tableName: TableName) => {
    const startTime = flags.debugMode ? Date.now() : 0;
    log.debug(
      `[useReactiveRelationalQuery] Executing reactive query ${debugName ?? querySummary} because table ${tableName} was updated`,
      { querySummary, debugName, tableName, querySql, time: startTime },
    );
    builtQuery.then((r) => {
      setCurrentResult(r);

      if (flags.debugMode) {
        const duration = Date.now() - startTime;
        QueryMetricsService.recordQueryExecution({
          queryName: debugName ?? querySummary,
          queryType: "reactive-relational",
          duration,
          querySummary,
          triggeringTable: tableName,
        });
      }
    });
  };

  const throttleQuery = useThrottledCallback(performQuery, throttleIntervalMs);

  useEffect(() => {
    const tables = getTablesFromRelationalQuery(builtQuery);

    throttleQuery(tables[0]);

    return TableEventManager.subscribe(tables, (tableName) => {
      throttleQuery(tableName);
    });
  }, [querySql, currentParams.current]);

  return currentResult;
}
